import Reveal from './Reveal';
import type { BuildPhase, BuildPhoto } from '../data/studioBuildLog';
import { BUILD_TOTAL_PHASES } from '../data/studioBuildLog';

function Photo({ photo, aspect }: { photo: BuildPhoto; aspect: string }) {
  return (
    <figure className="group">
      <div className={`relative w-full ${aspect} overflow-hidden bg-gray-100 border border-gray-200`}>
        <img
          src={photo.src}
          alt={photo.alt}
          title={`${photo.alt}｜スタジオができるまで`}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
        />
      </div>
      {photo.caption ? (
        <figcaption className="mt-3 text-gray-500 text-xs md:text-sm leading-relaxed border-l-2 border-teal-500 pl-3">
          {photo.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

interface StudioBuildPhaseProps {
  phase: BuildPhase;
}

export default function StudioBuildPhase({ phase }: StudioBuildPhaseProps) {
  const step = String(phase.step).padStart(2, '0');
  const stepTotal = String(BUILD_TOTAL_PHASES).padStart(2, '0');
  const [mainPhoto, ...restPhotos] = phase.photos;

  return (
    <article id={phase.id} className="scroll-mt-24">
      <Reveal>
        <div className="border-b border-gray-300 pb-5 mb-8 md:mb-10">
          <p className="flex items-center gap-2 text-teal-600 text-xs md:text-sm font-semibold tracking-[0.25em]">
            <span>STEP {step}</span>
            <span className="text-gray-400">/ {stepTotal}</span>
          </p>
          <h3 className="mt-3 font-mincho text-2xl md:text-3xl text-gray-900 leading-snug">
            {phase.title}
          </h3>
          <p className="mt-4 max-w-2xl text-gray-600 leading-relaxed text-sm md:text-base">
            {phase.lead}
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
        {mainPhoto ? (
          <Reveal className="sm:col-span-3" delay={0}>
            <Photo photo={mainPhoto} aspect="aspect-[16/9]" />
          </Reveal>
        ) : null}

        {restPhotos.map((photo, i) => (
          <Reveal key={`${phase.id}-photo-${i + 1}`} delay={80 + i * 80}>
            <Photo photo={photo} aspect="aspect-[4/3]" />
          </Reveal>
        ))}
      </div>
    </article>
  );
}
